/**
 * Album Types
 *
 * Type definitions for photo albums and media.
 * Follows the same patterns as other feature types.
 *
 * @module features/albums/types/album.types
 */

// ============================================
// Photo Types
// ============================================

/**
 * Photo / album visibility, as the UI is allowed to express it.
 *
 * `'private'` USED TO BE HERE AND IS GONE ON PURPOSE. It was enforced at no
 * layer and never had been: no RLS policy, RPC, view, trigger or edge function
 * reads `photos.visibility` or `albums.visibility` at all, and the only code
 * that looked at it was `AlbumsPage`'s client-side filter — which is display,
 * not access control. A parent who marked an album „Лично" was told something
 * untrue about what the database would do, and the album stayed readable by
 * every member of the family exactly like a `'family'` one. Offering the option
 * was worse than not offering it, so the option is withdrawn rather than
 * half-implemented.
 *
 * THE POSTGRES ENUM VALUE SURVIVES, deliberately, and is now unreachable from
 * the UI. Postgres has no `alter type ... drop value`, so removing it would
 * mean recreating `photo_visibility` and rewriting both live tables that use
 * it — a real outage window, for zero security gain, since the value never
 * gated anything. `src/shared/types/database.ts` therefore still lists all
 * three, and `normalizeVisibility` in supabaseAlbumService maps a legacy
 * `'private'` row onto `'family'` on the way in: `'family'` is the truthful
 * description of what that row has always meant.
 *
 * If per-album access control is wanted later it needs a policy, not this
 * column back.
 */
export type PhotoVisibility = 'family' | 'public';

/**
 * Bulgarian labels for photo visibility
 */
export const PHOTO_VISIBILITY_LABELS: Readonly<
  Record<PhotoVisibility, string>
> = {
  family: 'Семейство',
  public: 'Публично',
} as const;

/**
 * Photo metadata from EXIF or upload
 */
export interface PhotoMetadata {
  /** Original filename */
  readonly fileName?: string;

  /** File size in bytes */
  readonly fileSize?: number;

  /** MIME type */
  readonly mimeType?: string;

  /** Image dimensions */
  readonly width?: number;
  readonly height?: number;

  /** Camera/device info */
  readonly cameraMake?: string;
  readonly cameraModel?: string;

  /** GPS coordinates if available */
  readonly latitude?: number;
  readonly longitude?: number;

  /** Original date from EXIF */
  readonly dateTaken?: string;
}

/**
 * Individual photo in an album
 */
export interface Photo {
  /** Unique identifier */
  readonly id: string;

  /** Reference to parent album */
  readonly albumId: string;

  /**
   * Raw storage key of the full-size object, `<family_id>/<album_id>/<file>`.
   *
   * This is what `public.photos.url` actually holds since migration 020 — the
   * same convention `event_attachments.file_url` already used and what
   * `storageService`'s `buildObjectPath()` produces. It is stable, permanent
   * and safe to persist; it is NOT something you can put in an `<img src>`.
   */
  readonly storagePath: string;

  /**
   * A URL you can put in `src` — unchanged in meaning for every component.
   *
   * Since the `photos` bucket went private this is a SIGNED URL minted at read
   * time from {@link storagePath}, valid for `SIGNED_URL_TTL_SECONDS`. It is
   * never persisted anywhere: a signed URL in a `not null` column would expire,
   * and the offline queue would replay a pre-expired one out of IndexedDB.
   *
   * `''` when signing failed (object gone, policy refused, offline). Render
   * sites must treat an empty string and an `onError` the same way and show a
   * placeholder — see SignedImage.
   *
   * THERE IS NO `thumbnailUrl` AND NO `thumbnailPath`, and there is no smaller
   * variant hiding behind this one. A photo is ONE stored object; the grid, the
   * viewer and the cover all render this URL. `photos.thumbnail_url` held a
   * second copy of the same key on every row it ever had; nothing reads or
   * writes it any more, and the column itself is still on the table until
   * migration 025 — parked in `todo/pending-migrations/` — is pushed. See the
   * module banner of `supabaseAlbumService` for why a private bucket makes a
   * stored thumbnail unrepresentable rather than merely unused.
   */
  readonly url: string;

  /** Optional caption/description */
  readonly caption?: string;

  /** Tags for searching */
  readonly tags?: readonly string[];

  /** Member IDs of people tagged in photo */
  readonly taggedMembers?: readonly string[];

  /** Who uploaded this photo (member ID) */
  readonly uploadedBy: string;

  /** Photo visibility */
  readonly visibility: PhotoVisibility;

  /** Is this the album cover */
  readonly isCover: boolean;

  /** Optional metadata */
  readonly metadata?: PhotoMetadata;

  /** When the photo was taken */
  readonly takenAt?: string;

  /** When uploaded */
  readonly uploadedAt: string;

  /** Last update timestamp */
  readonly updatedAt: string;
}

// ============================================
// Album Types
// ============================================

/**
 * Album category for organization
 */
export type AlbumCategory =
  | 'family'
  | 'vacation'
  | 'birthday'
  | 'holiday'
  | 'school'
  | 'sports'
  | 'pets'
  | 'other';

/**
 * Bulgarian labels for album categories
 */
export const ALBUM_CATEGORY_LABELS: Readonly<Record<AlbumCategory, string>> = {
  family: 'Семейство',
  vacation: 'Ваканция',
  birthday: 'Рожден ден',
  holiday: 'Празник',
  school: 'Училище',
  sports: 'Спорт',
  pets: 'Домашни любимци',
  other: 'Други',
} as const;

/**
 * Album category with color and emoji
 */
export const ALBUM_CATEGORY_CONFIG: Readonly<
  Record<AlbumCategory, { color: string; emoji: string }>
> = {
  family: { color: 'hsl(var(--destructive))', emoji: '👨‍👩‍👧‍👦' },
  vacation: { color: 'hsl(var(--info))', emoji: '🏖️' },
  birthday: { color: 'hsl(var(--warning))', emoji: '🎂' },
  holiday: { color: 'hsl(var(--success))', emoji: '🎄' },
  school: { color: 'hsl(var(--primary))', emoji: '📚' },
  sports: { color: 'hsl(34 80% 50%)', emoji: '⚽' },
  pets: { color: 'hsl(280 60% 50%)', emoji: '🐾' },
  other: { color: 'hsl(var(--muted-foreground))', emoji: '📷' },
} as const;

/**
 * Photo album containing multiple photos
 */
export interface Album {
  /** Unique identifier */
  readonly id: string;

  /** Album title */
  readonly title: string;

  /** Album description */
  readonly description?: string | undefined;

  /** Album category */
  readonly category: AlbumCategory;

  /**
   * Emoji for visual identification.
   *
   * DERIVED, NOT STORED. `public.albums` has no `emoji` column; the service's
   * `rowToAlbum` fills this in from {@link ALBUM_CATEGORY_CONFIG} using the
   * persisted `category`, which is legitimate because the create/edit modal
   * only ever offers the category's own emoji. Still optional because an album
   * assembled locally (an optimistic write, a fixture) may not carry one.
   */
  readonly emoji?: string | undefined;

  /** Cover photo ID (or auto-select first photo) */
  readonly coverPhotoId?: string | undefined;

  /**
   * Signed URL of the cover photo, derived at read time — not a stored column.
   *
   * Carries the same expiry as any other signed URL, so it is only as fresh as
   * the fetch that produced it. Absent (rather than `''`) when there is no
   * cover or when signing failed, so `album.coverUrl ? … : placeholder` stays
   * the single check at the render site.
   */
  readonly coverUrl?: string | undefined;

  /** Total number of photos */
  readonly photoCount: number;

  /** Album visibility */
  readonly visibility: PhotoVisibility;

  /** Member IDs who can access this album */
  readonly memberIds: readonly string[];

  /** Who created the album (member ID) */
  readonly createdBy: string;

  /** Date range of photos in album */
  readonly dateRange?:
    | {
        readonly start: string;
        readonly end: string;
      }
    | undefined;

  /** Is this album archived */
  readonly isArchived: boolean;

  /** When created */
  readonly createdAt: string;

  /** Last update timestamp */
  readonly updatedAt: string;
}

// ============================================
// Comment Types
// ============================================

/**
 * Comment on a photo
 */
export interface PhotoComment {
  /** Unique identifier */
  readonly id: string;

  /** Reference to photo */
  readonly photoId: string;

  /** Comment text */
  readonly text: string;

  /** Who wrote the comment (member ID) */
  readonly authorId: string;

  /** When posted */
  readonly createdAt: string;

  /** When last edited */
  readonly updatedAt?: string;
}

// ============================================
// Input Types (for forms/API)
// ============================================

/**
 * Input for creating a new album
 */
export interface CreateAlbumInput {
  readonly title: string;
  readonly description?: string | undefined;
  readonly category: AlbumCategory;
  /**
   * Carried by the form (which sets it from the category) but NOT written:
   * there is no `albums.emoji` column, and {@link Album.emoji} is derived from
   * `category` on the way back out. Kept on the input so the modal's schema
   * keeps type-checking — and that is now the ONLY thing keeping it: since
   * `albumStore` started deriving the optimistic album's emoji from
   * `input.category` too, this field has one writer (CreateAlbumModal) and no
   * reader at all. It is a candidate for deletion, form field included.
   */
  readonly emoji?: string | undefined;
  readonly visibility?: PhotoVisibility | undefined;
  readonly memberIds?: readonly string[] | undefined;
}

/**
 * Input for updating an album
 */
export interface UpdateAlbumInput {
  readonly title?: string | undefined;
  readonly description?: string | undefined;
  readonly category?: AlbumCategory | undefined;
  /** Ignored by the service — see {@link CreateAlbumInput.emoji}. */
  readonly emoji?: string | undefined;
  readonly coverPhotoId?: string | undefined;
  readonly visibility?: PhotoVisibility | undefined;
  readonly memberIds?: readonly string[] | undefined;
  readonly isArchived?: boolean | undefined;
}

/**
 * Input for adding a photo.
 *
 * `url` is a RAW STORAGE KEY here, not a URL — it is what lands in
 * `photos.url`. The name is kept because it mirrors the column name; the doc
 * comment is the contract.
 *
 * There is no `thumbnailUrl` sibling any more. It existed only to carry the
 * exact same key a second time into `photos.thumbnail_url`, and `addPhoto` no
 * longer names that column at all — migration 024 gave it a `''` default so the
 * omission inserts cleanly. The column is dropped by migration 025, which is
 * parked in `todo/pending-migrations/` and pushed only after `pages:deploy`.
 */
export interface AddPhotoInput {
  readonly albumId: string;

  /** Raw storage key of the uploaded object (`<family_id>/<album_id>/<file>`). */
  readonly url: string;

  /**
   * A signed URL for the object that was just uploaded, for OPTIMISTIC RENDER
   * ONLY. Never persisted, and the Supabase service explicitly ignores it.
   *
   * It exists because the store's optimistic photo has to render something in
   * the window between "binary uploaded" and "row inserted, signed and
   * reconciled". Without it that window shows the placeholder for one
   * round-trip, and putting the raw key in `url` instead would issue a
   * relative-URL request against the app's own origin and 404.
   */
  readonly previewUrl?: string;

  readonly caption?: string;
  readonly tags?: readonly string[];
  readonly taggedMembers?: readonly string[];
  readonly visibility?: PhotoVisibility;
  readonly takenAt?: string;
  readonly metadata?: PhotoMetadata;
}

/**
 * Input for updating a photo
 */
export interface UpdatePhotoInput {
  readonly caption?: string;
  readonly tags?: readonly string[];
  readonly taggedMembers?: readonly string[];
  readonly visibility?: PhotoVisibility;
  readonly isCover?: boolean;
}

// ============================================
// View State Types
// ============================================

/**
 * Album sort options
 */
export type AlbumSortBy = 'title' | 'createdAt' | 'updatedAt' | 'photoCount';

/**
 * Photo sort options
 */
export type PhotoSortBy = 'takenAt' | 'uploadedAt' | 'caption';

/**
 * Sort direction
 */
export type SortDirection = 'asc' | 'desc';

/**
 * Album filter state
 */
export interface AlbumFilters {
  readonly category?: AlbumCategory;
  readonly visibility?: PhotoVisibility;
  readonly search?: string;
  readonly showArchived?: boolean;
}

/**
 * Photo filter state
 */
export interface PhotoFilters {
  readonly visibility?: PhotoVisibility;
  readonly taggedMember?: string;
  readonly search?: string;
  readonly dateFrom?: string;
  readonly dateTo?: string;
}

// ============================================
// Default Values
// ============================================

/**
 * Default album settings
 */
export const DEFAULT_ALBUM_SETTINGS = {
  visibility: 'family' as PhotoVisibility,
  category: 'family' as AlbumCategory,
  isArchived: false,
} as const;

/**
 * Default photo settings
 */
export const DEFAULT_PHOTO_SETTINGS = {
  visibility: 'family' as PhotoVisibility,
  isCover: false,
} as const;
